import { useState, useEffect } from 'react';
import { X, Sparkles, Flame, Target, Handshake, Rocket } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const HOODIES = [
  {
    id: 'hoodie-logo',
    name: 'CWF Logo Hoodie',
    dosage: 'Front Only or Front + Back',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_5be9d9ba-e756-4119-8306-729d4aea9d0d/artifacts/1tfhnbh6_99A28C9F-E518-451D-91EA-DB917F549E13.png',
  },
  {
    id: 'hoodie-patch',
    name: 'CWF Patch Hoodie',
    dosage: 'Leather Patch',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_5be9d9ba-e756-4119-8306-729d4aea9d0d/artifacts/eyh7nlze_2AEC4055-2288-407E-B0B6-08F58E06B397.png',
  },
];

const STORAGE_KEY = 'whats-new-referral-event-dismissed';

export const WhatsNewPopup = () => {
  const [visible, setVisible] = useState(false);
  const [event, setEvent] = useState(null);
  const location = useLocation();
  const hiddenRoutes = ['/admin', '/cart'];

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    let timer;
    Promise.all([
      axios.get(`${API}/promo-config`).catch(() => ({ data: null })),
      axios.get(`${API}/referral-event`).catch(() => ({ data: null })),
    ]).then(([promoRes, eventRes]) => {
      setEvent(eventRes.data);
      if (promoRes.data?.active && promoRes.data?.free_item) return;
      timer = setTimeout(() => setVisible(true), 1200);
    });
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setVisible(false);
    sessionStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!visible || hiddenRoutes.includes(location.pathname)) return null;

  const eventActive = !!event?.active;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="whats-new-popup-overlay">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      <div
        className="relative w-full max-w-md bg-[#0A0A0A] border-2 border-[#D4AF37] rounded-lg overflow-hidden animate-fade-in-up shadow-2xl"
        style={{ boxShadow: '0 0 35px rgba(212,175,55,0.35)' }}
        data-testid="whats-new-popup"
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 text-gray-400 hover:text-white transition-colors"
          data-testid="whats-new-popup-close"
        >
          <X size={22} />
        </button>

        <div className="h-1.5 bg-gradient-to-r from-[#D4AF37] via-white to-[#D4AF37]" />

        <div className="px-6 pt-7 pb-6 text-center">
          {eventActive ? (
            <>
              <div className="inline-flex items-center gap-2 bg-[#C41E3A]/15 border border-[#C41E3A]/50 rounded-full px-4 py-1.5 mb-4">
                <Flame size={14} className="text-[#C41E3A]" />
                <span className="text-[#C41E3A] text-xs font-bold uppercase tracking-widest">Limited Time Event</span>
              </div>

              <h2 className="font-['Oswald'] text-3xl font-bold text-white tracking-tight uppercase leading-tight" data-testid="referral-event-title">
                2 for 1 <span className="text-[#C41E3A]">Point Event</span> is Here!
              </h2>
              <p className="text-gray-300 text-sm mt-2">
                It&apos;s time to bring your friends and get rewarded!
              </p>

              <div className="space-y-2.5 mt-5 text-left">
                <div className="flex items-center gap-3 bg-[#121212] border border-[#27272A] rounded-sm px-4 py-3">
                  <Flame size={18} className="text-[#C41E3A] flex-shrink-0" />
                  <p className="text-sm text-gray-200">Every successful referral = <span className="text-[#D4AF37] font-bold">2 points</span></p>
                </div>
                <div className="flex items-center gap-3 bg-[#121212] border border-[#27272A] rounded-sm px-4 py-3">
                  <Target size={18} className="text-[#D4AF37] flex-shrink-0" />
                  <p className="text-sm text-gray-200">100 points = <span className="text-[#C41E3A] font-bold">20% OFF</span></p>
                </div>
                <div className="flex items-center gap-3 bg-[#121212] border border-[#27272A] rounded-sm px-4 py-3">
                  <Handshake size={18} className="text-green-500 flex-shrink-0" />
                  <p className="text-sm text-gray-200">Bring a friend. Earn points. Get rewarded.</p>
                </div>
              </div>

              <p className="text-gray-400 text-xs leading-relaxed mt-4 mb-5 flex items-center justify-center gap-1.5">
                <Rocket size={13} className="text-[#D4AF37]" />
                The more people you refer, the faster you reach your goal!
              </p>

              <Link
                to="/#products"
                onClick={handleClose}
                className="inline-block w-full bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider py-3.5 rounded-md transition-all duration-300"
                data-testid="whats-new-popup-shop-btn"
              >
                Start Earning
              </Link>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-full px-4 py-1.5 mb-4">
                <Sparkles size={14} className="text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest">What's New</span>
              </div>

              <div className="flex justify-center gap-3 mb-4">
                {HOODIES.map((h) => (
                  <Link
                    key={h.id}
                    to={`/product/${h.id}`}
                    onClick={handleClose}
                    className="w-36 rounded-md overflow-hidden border border-[#D4AF37]/40 hover:border-[#D4AF37] transition-colors bg-[#121212]"
                    data-testid={`whats-new-popup-image-${h.id}`}
                  >
                    <img src={h.image} alt={h.name} className="w-36 h-36 object-cover" />
                    <p className="text-gray-300 text-[10px] uppercase tracking-wider py-1.5 px-1">{h.dosage}</p>
                  </Link>
                ))}
              </div>

              <h2 className="font-['Oswald'] text-4xl font-bold text-white tracking-tight uppercase">
                CWF <span className="text-[#C41E3A]">Hoodies</span>
              </h2>
              <p className="text-gray-400 text-sm uppercase tracking-wider mt-1">Premium Heavyweight Fleece</p>
              <p className="text-[#D4AF37] text-sm font-bold uppercase tracking-wider mt-2">Sizes XS – 2XL</p>
              <p className="text-white text-3xl font-bold mt-3" data-testid="whats-new-popup-price">$65</p>
              <p className="text-gray-400 text-xs leading-relaxed mt-3 mb-5">
                Two styles available — stitched leather patch or logo print (front only, or front + back).
              </p>

              <Link
                to="/product/hoodie-logo"
                onClick={handleClose}
                className="inline-block w-full bg-[#C41E3A] hover:bg-[#A01830] text-white font-bold uppercase tracking-wider py-3.5 rounded-md transition-all duration-300"
                data-testid="whats-new-popup-shop-btn"
              >
                Shop Hoodies
              </Link>
            </>
          )}
        </div>

        <div className="h-1.5 bg-gradient-to-r from-[#D4AF37] via-white to-[#D4AF37]" />
      </div>
    </div>
  );
};

export default WhatsNewPopup;
